import { anatomyToVector } from "./index6.js";
import { hasBoneAt } from "./index20.js";
import { hasCornerCore } from "./index22.js";
import { isBoneSprinklered } from "./index29.js";
import { BoneSide, uuidV4 } from "@project-rouge/rg-core";
import { MuscleToVector, Vectors } from "@project-rouge/rg-three";
import { Vector3 } from "three";
var MuscleShiftKey = /* @__PURE__ */ function(MuscleShiftKey$1) {
	MuscleShiftKey$1["n3"] = "n3";
	MuscleShiftKey$1["n2"] = "n2";
	MuscleShiftKey$1["n1"] = "n1";
	MuscleShiftKey$1["zero"] = "zero";
	MuscleShiftKey$1["p1"] = "p1";
	MuscleShiftKey$1["p2"] = "p2";
	return MuscleShiftKey$1;
}(MuscleShiftKey || {});
var _vectorA = new Vector3();
var _vectorB = new Vector3();
var shiftWithCloseEnds = [MuscleShiftKey.zero, MuscleShiftKey.p2];
var muscleLocksDict = {
	[MuscleShiftKey.n3]: true,
	[MuscleShiftKey.n2]: true,
	[MuscleShiftKey.n1]: true,
	[MuscleShiftKey.zero]: false,
	[MuscleShiftKey.p1]: true,
	[MuscleShiftKey.p2]: false
};
function AddMusclesToBone(bone, aggConfig) {
	const { corridorWidth, moduleDepth, cornerCore, gap } = aggConfig;
	const useGrid = !!aggConfig.gridCellWidth;
	const maxTravelDist = isBoneSprinklered(bone, aggConfig) ? aggConfig.maxTravelDistSprinklered : aggConfig.maxTravelDist;
	const maxCoreToCoreDist = aggConfig.maxTravelDistBidirectional * 2;
	const shiftOptions = {
		[MuscleShiftKey.n3]: -corridorWidth * .5 - cornerCore.size[0] - gap * .5,
		[MuscleShiftKey.n2]: -corridorWidth * .5 - cornerCore.size[1] - gap * .5,
		[MuscleShiftKey.n1]: -corridorWidth * .5,
		[MuscleShiftKey.zero]: 0,
		[MuscleShiftKey.p1]: corridorWidth * .5 - gap * .5,
		[MuscleShiftKey.p2]: corridorWidth * .5 + moduleDepth
	};
	if (bone.loads[BoneSide.Right]) {
		const { muscle, shifts } = addMuscle({
			bone,
			corridorWidth,
			useGrid,
			right: true
		});
		shiftMuscle(muscle, shifts, shiftOptions);
		setMuscleLocks(muscle, shifts);
		setMuscleTravelDist(muscle, maxTravelDist, maxCoreToCoreDist);
		setMuscleCloseEnds(muscle, shifts, useGrid);
		setSkipCores(muscle);
	}
	if (bone.loads[BoneSide.Left]) {
		const { muscle, shifts } = addMuscle({
			bone,
			corridorWidth,
			useGrid,
			right: false
		});
		shiftMuscle(muscle, shifts, shiftOptions);
		setMuscleLocks(muscle, shifts);
		setMuscleTravelDist(muscle, maxTravelDist, maxCoreToCoreDist);
		setMuscleCloseEnds(muscle, shifts, useGrid);
		setSkipCores(muscle);
	}
	if (bone.muscles.every((m) => m.skipCores)) (bone.muscles[1] || bone.muscles[0]).skipCores = false;
}
function addMuscle({ bone, corridorWidth, right, useGrid }) {
	const shifts = [MuscleShiftKey.zero, MuscleShiftKey.zero];
	const muscle = createMuscle(bone, right ? BoneSide.Right : BoneSide.Left);
	initMusclePoints({
		muscle,
		corridorWidth,
		right
	});
	bone.muscles.push(muscle);
	if (hasBoneAt({
		bone,
		right: !right,
		start: right,
		outerLoad: false
	})) shifts[0] = MuscleShiftKey.p1;
	else if (hasBoneAt({
		bone,
		right,
		start: right,
		innerLoad: false
	})) shifts[0] = MuscleShiftKey.n1;
	else if (hasBoneAt({
		bone,
		right: !right,
		start: right,
		outerLoad: true
	})) shifts[0] = useGrid ? MuscleShiftKey.p1 : MuscleShiftKey.n1;
	else if (hasBoneAt({
		bone,
		right,
		start: right,
		innerLoad: true
	})) shifts[0] = MuscleShiftKey.n3;
	if (hasBoneAt({
		bone,
		right: !right,
		start: !right,
		outerLoad: true
	})) shifts[1] = MuscleShiftKey.p2;
	else if (hasBoneAt({
		bone,
		right: !right,
		start: right,
		outerLoad: false
	})) shifts[1] = MuscleShiftKey.p1;
	else if (hasBoneAt({
		bone,
		right: !right,
		start: !right,
		outerLoad: false
	})) shifts[1] = MuscleShiftKey.p1;
	else if (hasBoneAt({
		bone,
		right,
		start: !right,
		innerLoad: false
	})) shifts[1] = MuscleShiftKey.n1;
	else if (hasBoneAt({
		bone,
		right,
		start: !right,
		innerLoad: true
	})) shifts[1] = MuscleShiftKey.n2;
	return {
		muscle,
		shifts
	};
}
function shiftMuscle(muscle, shifts, shiftOptions) {
	const mVector = MuscleToVector(muscle, _vectorA).normalize();
	muscle.points[0] = _vectorB.fromArray(muscle.points[0]).addScaledVector(mVector, -shiftOptions[shifts[0]]).toArray();
	muscle.points[1] = _vectorB.fromArray(muscle.points[1]).addScaledVector(mVector, shiftOptions[shifts[1]]).toArray();
}
function setSkipCores(muscle) {
	if (isSingleLoad(muscle.bone)) {
		muscle.skipCores = false;
		return;
	}
	muscle.skipCores = !isInnerMuscle(muscle);
}
function isSingleLoad(bone) {
	return bone.loads.filter((load) => load).length === 1;
}
function isInnerMuscle(muscle) {
	if (hasBoneAt({
		bone: muscle.bone,
		right: muscle.side === BoneSide.Right,
		start: true,
		innerLoad: true
	})) return true;
	if (hasBoneAt({
		bone: muscle.bone,
		right: muscle.side === BoneSide.Right,
		start: false,
		innerLoad: true
	})) return true;
	return false;
}
function setMuscleCloseEnds(muscle, shifts, useGrid) {
	muscle.canCloseEnds[0] = shiftWithCloseEnds.includes(shifts[0]);
	muscle.canCloseEnds[1] = shiftWithCloseEnds.includes(shifts[1]);
	if (!useGrid) return;
	if (muscle.bone.joints[0].bones.length > 1) muscle.canCloseEnds[muscle.side === BoneSide.Right ? 0 : 1] = false;
	if (muscle.bone.joints[1].bones.length > 1) muscle.canCloseEnds[muscle.side === BoneSide.Right ? 1 : 0] = false;
}
function setMuscleLocks(muscle, shifts) {
	if (muscle.bone.joints.every((j) => j.bones.length === 1)) {
		muscle.lockEnds[0] = muscle.side === BoneSide.Right;
		muscle.lockEnds[1] = muscle.side !== BoneSide.Right;
		return;
	}
	muscle.lockEnds[0] = muscleLocksDict[shifts[0]];
	muscle.lockEnds[1] = muscleLocksDict[shifts[1]];
	if (!muscle.lockEnds[0] && !muscle.lockEnds[1]) muscle.lockEnds[0] = true;
	if (muscle.lockEnds[0] && muscle.lockEnds[1]) muscle.lockEnds[1] = false;
}
function setMuscleTravelDist(muscle, travelDist, maxCoreToCoreDist) {
	muscle.corridorDist.end = travelDist;
	muscle.corridorDist.middle = maxCoreToCoreDist;
	muscle.corridorDist.start = travelDist;
	const coreAtStart = hasCornerCore({
		bone: muscle.bone,
		start: true
	});
	const coreAtEnd = hasCornerCore({
		bone: muscle.bone,
		start: false
	});
	if (muscle.side === BoneSide.Right) {
		if (coreAtStart) muscle.corridorDist.start = maxCoreToCoreDist;
		if (coreAtEnd) muscle.corridorDist.end = maxCoreToCoreDist;
	} else {
		if (coreAtStart) muscle.corridorDist.end = maxCoreToCoreDist;
		if (coreAtEnd) muscle.corridorDist.start = maxCoreToCoreDist;
	}
}
function initMusclePoints({ muscle, corridorWidth, right }) {
	const side = anatomyToVector(muscle.bone, _vectorA).cross(Vectors.Up).normalize().multiplyScalar(corridorWidth * .5);
	if (!right) side.negate();
	muscle.points[0] = _vectorB.fromArray(muscle.bone.joints[0].pos).add(side).toArray();
	muscle.points[1] = _vectorB.fromArray(muscle.bone.joints[1].pos).add(side).toArray();
	if (muscle.flip) muscle.points.reverse();
}
function createMuscle(bone, side) {
	return {
		bone,
		side,
		id: uuidV4(),
		points: [[
			0,
			0,
			0
		], [
			0,
			0,
			0
		]],
		flip: side === BoneSide.Left,
		lockEnds: [false, false],
		canCloseEnds: [false, false],
		skipCores: false,
		gFUnits: [],
		fireCores: 0,
		liftCores: 0,
		stairCores: 0,
		corridorDist: {
			start: 0,
			middle: 0,
			end: 0
		},
		realLength: 0
	};
}
export { AddMusclesToBone };

//# sourceMappingURL=index30.js.map